/**
 * MapOverlay Component
 *
 * Displays store information overlay on the left half of the map.
 * Shows address, directions button, wheelchair access, and parking info.
 */

import React from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Button,
  HStack,
  Text,
  useMultiStyleConfig,
  VStack,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {ParkingIcon, WheelchairIcon} from '@amblique/king-living/app/components/icons';

/**
 * MapOverlay component
 *
 * @param {Object} props - Component props
 * @param {Object} props.store - SFCC Store data
 * @param {boolean} props.wheelchairAccessible - Wheelchair accessible flag from Contentful
 * @param {string} props.parkingAddress - Parking address from Contentful
 * @returns {React.ReactElement}
 */
export const MapOverlay = ({
  parkingAddress = '',
  store,
  wheelchairAccessible = false,
  ...props
}) => {
  const styles = useMultiStyleConfig('contentful/showroom-details');

  if (!store) {
    return null;
  }

  // Build full address string
  const fullAddress = [
    store.address1,
    store.address2,
    store.city,
    store.stateCode,
    store.postalCode
  ].filter(Boolean).join(', ');

  // Handle get directions click - opens Google Maps directions
  // Uses store name + address for better display in Google Maps destination field
  const handleGetDirections = () => {
    // Build destination string with store name for better display
    // Format: "Store Name, Full Address" - this displays nicely in Google Maps
    const destinationParts = [store.name, fullAddress].filter(Boolean);
    const destination = encodeURIComponent(destinationParts.join(', '));

    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;

    window.open(mapsUrl, '_blank', 'noopener,noreferrer');
  };

  const hasParkingInfo = !!parkingAddress;
  const hasAccessibilityInfo = wheelchairAccessible || hasParkingInfo;

  return (
    <Box
      className="map-overlay"
      {...styles.mapOverlay}
      {...props}
    >
      <VStack align="stretch" spacing={{base: '16px', lg: '16x'}}>
        {/* Address Header */}
        <Text {...styles.mapOverlayAddress}>
          {fullAddress}
        </Text>

        {/* Get Direction Button */}
        <Button
          {...styles.mapOverlayButton}
          onClick={handleGetDirections}
        >
          Get direction
        </Button>

        <Box as="span" {...styles.mapOverlayDivider} />

        {/* Accessibility & Parking Info */}
        {hasAccessibilityInfo && (
          <VStack {...styles.mapOverlayInfoSection}>
            {/* Wheelchair Access */}
            {wheelchairAccessible && (
              <HStack {...styles.mapOverlayInfoItem}>
                <WheelchairIcon {...styles.mapOverlayIcon} />
                <Text {...styles.mapOverlayInfoText}>
                  Wheelchair access
                </Text>
              </HStack>
            )}

            {/* Parking Info */}
            {hasParkingInfo && (
              <VStack {...styles.mapOverlayParkingContainer}>
                <HStack {...styles.mapOverlayInfoItem}>
                  <ParkingIcon {...styles.mapOverlayIcon} />
                  <Text {...styles.mapOverlayInfoText}>
                    Carpark available
                  </Text>
                </HStack>
                <Text {...styles.mapOverlayInfoText}>
                  {parkingAddress}
                </Text>
              </VStack>
            )}
          </VStack>
        )}
      </VStack>
    </Box>
  );
};

MapOverlay.displayName = 'MapOverlay';

MapOverlay.propTypes = {
  parkingAddress: PropTypes.string,
  store: PropTypes.shape({
    address1: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    name: PropTypes.string,
    postalCode: PropTypes.string,
    stateCode: PropTypes.string,
  }),
  wheelchairAccessible: PropTypes.bool,
};

export default MapOverlay;
